import type { ReactNode } from 'react'
import { Typography, type TypographyVariant } from '@components/atoms/Typography'
import styles from './PageTitleBar.module.scss'

export type PageTitleBarVariant = 'lg' | 'xl' | 'md'

export interface PageTitleBarProps {
  title: string
  /**
   * Visual scale of the title row.
   * - `'xl'` (default) — list / landing pages. `title-xl` semibold (24/32),
   *   64 px tall row. Matches every existing call-site.
   * - `'lg'` — compact detail-page header. `title-lg` semibold (20/28),
   *   56 px tall row. Pairs with `leadingSlot` for the back / record-id
   *   header on detail pages (Figma node `926:14957`).
   */
  variant?: PageTitleBarVariant
  /**
   * Optional left-aligned slot rendered before the title. Typically a back
   * `ButtonUtility`. Only the `'lg'` variant is currently designed to host
   * one, but the slot is honoured for both variants so callers can opt in.
   */
  leadingSlot?: ReactNode
  /** Optional right-aligned slot for action buttons (filter, sort, more). */
  rightSlot?: ReactNode
  className?: string
}

const TITLE_TYPOGRAPHY_VARIANT: Record<PageTitleBarVariant, TypographyVariant> =
  {
    xl: 'title-xl',
    lg: 'title-lg',
    md: 'title-md',
  }

export function PageTitleBar({
  title,
  variant = 'xl',
  leadingSlot,
  rightSlot,
  className,
}: PageTitleBarProps) {
  const rootClasses = [
    styles.root,
    styles[`variant-${variant}`],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClasses}>
      {leadingSlot ? (
        <div className={styles.leadingSlot}>{leadingSlot}</div>
      ) : null}
      <Typography
        variant={TITLE_TYPOGRAPHY_VARIANT[variant]}
        weight="semibold"
        color="primary"
        truncate
        className={styles.title}
      >
        {title}
      </Typography>
      {rightSlot ? <div className={styles.rightSlot}>{rightSlot}</div> : null}
    </div>
  )
}
